<template>
  <div>
    <b-button
      v-if="hasUserPermission"
      variant="outline-primary"
      @click="onClickHandler"
    >
      {{ $t(`${tBase}.update-life-expectancy`) }}
    </b-button>
    <s-modal
      v-model="showModal"
      cancel-confirm-message-code="MC006"
      ok-confirm-message-code="MC048"
      :size="modalSize"
      :title="$t(`${tBase}.update-life-expectancy`)"
      :cancel-disabled="loading"
      :ok-disabled="isOkDisabled"
      :ok-title="$t('general.confirm')"
      :is-loading="loading"
      :confirm-type="confirmationTypes.YES_NO"
      @cancel="cancel"
      @ok="save"
      @show="show"
    >
      <p class="text-color-primary">
        <strong>{{ $t(`${tBase}.last-update-reported`) }}</strong>
      </p>
      <b-row class="mb-3">
        <b-col cols="6">
          <strong>{{ $t('general.user') }}:</strong>
          <span class="text-uppercase">{{ lifeExpectancy.user }}</span>
        </b-col>
        <b-col
          cols="6"
          class="text-right"
        >
          <strong>{{ $t('general.date-time') }}:</strong>
          <date-time-label
            :value="lifeExpectancy.dateTime"
            :format="dateTimeLabelFormat"
          />
        </b-col>
      </b-row>
      <div class="gray-box mb-4">
        <b-row align-h="between">
          <b-col
            class="text-center"
            xs="4"
          >
            <strong>{{ $t('fields.engineering.winding') }}</strong>
          </b-col>
          <b-col
            class="text-center"
            xs="4"
          >
            <strong>{{ $t(`${tFields}.in-years`) }}</strong>
          </b-col>
          <b-col
            class="text-center"
            xs="4"
          >
            <strong>{{ $t(`${tFields}.in-percentage`) }}</strong>
          </b-col>
        </b-row>
        <b-row
          align-h="between"
          v-for="item in lifeExpectancy.windings"
          :key="item.id"
        >
          <b-col
            class="text-center"
            xs="4"
          >
            {{ item.windingId }}
          </b-col>
          <b-col
            class="text-center"
            xs="4"
          >
            {{ getFormattedNumber(item.inYears, 'inYears') }}
          </b-col>
          <b-col
            class="text-center"
            xs="4"
          >
            {{ getFormattedNumber(item.inPercentage, 'inPercentage') }}
          </b-col>
        </b-row>
      </div>
      <hr/>
      <s-empty-list
        v-if="showEmptyList"
        class="py-4"
        :loading="loading"
      />
      <div v-if="!showEmptyList">
        <p class="text-color-secondary">
          <strong>{{ $t(`${tBase}.update-life-expectancy`) }}</strong>
        </p>
        <b-row class="mb-3">
          <b-col>
            <b-form-radio
              v-model="informationType"
              :value="informationTypes.IN_YEARS"
              :disabled="isDisabled"
              @change="onInformationTypeChange"
            >
              {{ $t(`${tFields}.inform-in-years`) }}
            </b-form-radio>
          </b-col>
          <b-col>
            <b-form-radio
              v-model="informationType"
              :value="informationTypes.IN_PERCENTAGE"
              :disabled="isDisabled"
              @change="onInformationTypeChange"
            >
              {{ $t(`${tFields}.inform-in-percentage`) }}
            </b-form-radio>
          </b-col>
        </b-row>
        <s-fieldset
          class="life-expectancy__fieldset"
          v-for="(winding, index) in windings"
          :key="winding.id"
          :legend="$t(`${tBase}.winding-life-expectancy-remaining`, {
              id: winding.windingId,
            })"
        >
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>{{ getLabel(informationTypes.IN_YEARS) }}</label>
                <s-number-input
                  class="text-right w-75"
                  v-model="$v.windings.$each[index].inYears.$model"
                  :numeric-options="getNumericOptions('inYears')"
                  :validation="$v.windings.$each[index]['inYears']"
                  :invalid="$v.windings.$each[index]['inYears'].$error"
                  :disabled="isInYearsDisabled"
                />
                <b-form-invalid-feedback
                  v-if="$v.windings.$each[index].inYears.$error"
                  id="life-expectancy-in-years-feedback"
                >
                  <span v-html="getValidationFeedback('inYears')"></span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>{{ getLabel(informationTypes.IN_PERCENTAGE) }}</label>
                <s-number-input
                  class="text-right w-75"
                  v-model="$v.windings.$each[index].inPercentage.$model"
                  :numeric-options="getNumericOptions('inPercentage')"
                  :validation="$v.windings.$each[index]['inPercentage']"
                  :invalid="$v.windings.$each[index]['inPercentage'].$error"
                  :disabled="isInPercentageDisabled"
                />
                <b-form-invalid-feedback
                  v-if="$v.windings.$each[index].inPercentage.$error"
                  id="life-expectancy-in-percentage-feedback"
                >
                  <span v-html="getValidationFeedback('inPercentage')"></span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </s-fieldset>
      </div>
    </s-modal>
  </div>
</template>

<script>
import { LifeExpectancyInformationType } from 'sigma-mappings';
import { mapGetters } from 'vuex';
import LoadingMixin from '@/services/loadingMixin';
import Service
  from '@/domain/services/installation/engineering/isolationAging/isolationAgingService';
import lifeExpectancyFields
  from '@/domain/data/installation/engineering/isolationAging/lifeExpectancyFields';
import validations
  from '@/domain/data/installation/engineering/isolationAging/lifeExpectancyValidations';
import confirmationTypes from '@/constants/confirmationTypes';
import { formats } from '@/constants/localizer';

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    windingsAmount: {
      type: Number,
      default: 0,
    },
  },
  mixins: [LoadingMixin],
  data: () => ({
    showModal: false,
    tBase: 'engineering.isolation-aging',
    tFields: 'fields.engineering.isolation-aging',
    dateTimeLabelFormat: formats.DATETIME_SHORT_WITH_SECONDS,
    confirmationTypes,
    informationTypes: LifeExpectancyInformationType,
    informationType: LifeExpectancyInformationType.IN_YEARS,
    lifeExpectancy: {},
    windings: [],
  }),
  validations() {
    return validations(this.informationType, this.lifeExpectancy.totalAssetLifeExpectancyInYears);
  },
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    modalSize() {
      return this.currentFontSize.includes('18') ? 'lg' : 'md';
    },
    hasUserPermission() {
      return this.$hasUserPermission('PER013');
    },
    isOkDisabled() {
      return this.$v.windings.$invalid || this.loading;
    },
    isDisabled() {
      return this.loading;
    },
    isInYearsDisabled() {
      return this.isDisabled
        || this.informationType === LifeExpectancyInformationType.IN_PERCENTAGE;
    },
    isInPercentageDisabled() {
      return this.isDisabled
        || this.informationType === LifeExpectancyInformationType.IN_YEARS;
    },
    showEmptyList() {
      return (this.loading && this.windings && !this.windings.length)
        || !this.windings
        || !this.windings.length;
    },
  },
  methods: {
    onClickHandler() {
      this.showModal = true;
    },
    getNumericOptions(fieldName) {
      return {
        decimalPlaces: lifeExpectancyFields[fieldName].precision,
      };
    },
    formatNumberValue(value, fieldName) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: lifeExpectancyFields[fieldName].precision,
        maximumFractionDigits: lifeExpectancyFields[fieldName].precision,
      });
    },
    getLabel(informationType) {
      const isRequired = informationType === this.informationType ? '*' : '';

      if (informationType === LifeExpectancyInformationType.IN_YEARS) {
        return this.$t(`${this.tFields}.in-years`) + isRequired;
      }

      return this.$t(`${this.tFields}.in-percentage`) + isRequired;
    },
    getValidationFeedback(fieldName) {
      return this.$t('messages.MA086.message',
        {
          minValue: this.formatNumberValue(lifeExpectancyFields[fieldName].minValue, fieldName),
          maxValue: this.formatNumberValue(lifeExpectancyFields[fieldName]
            .getMaxValue(this.lifeExpectancy.totalAssetLifeExpectancyInYears), fieldName),
        });
    },
    initWindings() {
      if (this.windings.length === this.windingsAmount) return;

      this.informationType = LifeExpectancyInformationType.IN_YEARS;
      this.lifeExpectancy = {};
      this.windings = [];

      for (let index = 1; index <= this.windingsAmount; index += 1) {
        this.windings.push({
          windingId: index,
          inYears: null,
          inPercentage: null,
        });
      }

      this.$v.$reset();
    },
    show() {
      this.startLoading();

      const { assetModuleId } = this.$route.params;
      Service.getLifeExpectancy(assetModuleId)
        .then((response) => {
          this.initWindings();
          this.lifeExpectancy = response;
        }).finally(() => {
        this.endLoading();
      });
    },
    onInformationTypeChange() {
      this.windings.forEach((item, index) => {
        (this.windings || [])[index].inYears = null;
        (this.windings || [])[index].inPercentage = null;
      });

      this.$v.$reset();
    },
    save() {
      this.startLoading();

      const { assetModuleId } = this.$route.params;
      const params = [];

      this.windings.forEach((item) => {
        params.push({
          ...item,
          informationType: this.informationType,
        });
      });

      Service.editLifeExpectancy(assetModuleId, params)
        .then(() => {
          this.$emit('life-expectancy-saved');
          this.$pushAlert({ code: 'MS032' });
          this.cancel();
        })
        .finally(() => {
          this.endLoading();
        });
    },
    cancel() {
      this.lifeExpectancy = {};
      this.windings = [];
      this.closeModal();
    },
    closeModal() {
      this.showModal = false;
    },
    getFormattedNumber(value, fieldName) {
      if (value == null || value === '') {
        return '-';
      }

      if (typeof value !== 'number') {
        return value;
      }

      return this.$n(value, {
        minimumFractionDigits: lifeExpectancyFields[fieldName].precision,
        maximumFractionDigits: lifeExpectancyFields[fieldName].precision,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
.life-expectancy
  &__fieldset
    margin-bottom: 1.5rem

    &:last-child
      margin-bottom: 0
</style>
